import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Power, PowerOff, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCategories = ({ token }) => {
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(0);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    slug: '',
    ordem: 0
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const generateSlug = (nome) => {
    return nome
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const categoryData = {
        ...formData,
        slug: formData.slug || generateSlug(formData.nome)
      };

      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Categoria atualizada!');
      } else {
        await axios.post(`${API}/categories`, categoryData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Categoria criada!');
      }

      setShowForm(false);
      setEditingCategory(null);
      setFormData({ nome: '', descricao: '', slug: '', ordem: 0 });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar categoria');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      nome: category.nome,
      descricao: category.descricao || '',
      slug: category.slug,
      ordem: category.ordem
    });
    setShowForm(true);
  };

  const handleToggleCategory = async (categoryId) => {
    try {
      const response = await axios.put(`${API}/categories/${categoryId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${response.data.message}. ${response.data.produtos_afetados} produtos afetados.`);
      fetchCategories();
    } catch (error) {
      toast.error('Erro ao alterar status da categoria');
    }
  };

  const startDeleteProcess = (category) => {
    setShowDeleteConfirm(category);
    setDeleteConfirmStep(1);
    setDeleteConfirmText('');
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
    setDeleteConfirmStep(0);
    setDeleteConfirmText('');
  };

  const handleDeleteStep = async () => {
    if (deleteConfirmStep === 1) {
      setDeleteConfirmStep(2);
    } else if (deleteConfirmStep === 2) {
      setDeleteConfirmStep(3);
    } else if (deleteConfirmStep === 3) {
      if (deleteConfirmText === 'DELETAR') {
        try {
          const response = await axios.delete(
            `${API}/categories/${showDeleteConfirm.id}?confirm=DELETAR-CATEGORIA-PERMANENTEMENTE`, 
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success(`${response.data.message}. ${response.data.produtos_deletados} produtos removidos.`);
          cancelDelete();
          fetchCategories();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Erro ao excluir categoria');
        }
      } else {
        toast.error('Digite DELETAR para confirmar');
      }
    }
  };

  // Modal de confirmação de exclusão
  const DeleteConfirmModal = () => {
    if (!showDeleteConfirm) return null;

    return (
      <>
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000
          }}
          onClick={cancelDelete}
        />
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '1.5rem',
          maxWidth: '400px',
          width: '90%',
          zIndex: 1001,
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <AlertTriangle size={48} style={{ color: '#ef4444', margin: '0 auto 1rem' }} />
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              Excluir Categoria
            </h3>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>
              "{showDeleteConfirm.nome}"
            </p>
          </div>

          {deleteConfirmStep === 1 && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ marginBottom: '1rem', color: '#ef4444', fontWeight: '500' }}>
                ATENÇÃO: Esta ação é IRREVERSÍVEL!
              </p>
              <p style={{ marginBottom: '1.5rem', fontSize: '0.875rem', color: '#666' }}>
                Todos os produtos desta categoria também serão excluídos permanentemente.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <Button variant="outline" onClick={cancelDelete} style={{ flex: 1 }}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleDeleteStep} 
                  style={{ flex: 1, backgroundColor: '#ef4444' }}
                >
                  Continuar (1/3)
                </Button>
              </div>
            </div>
          )}

          {deleteConfirmStep === 2 && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ marginBottom: '1rem', color: '#ef4444', fontWeight: '500' }}>
                Tem CERTEZA ABSOLUTA?
              </p>
              <p style={{ marginBottom: '1.5rem', fontSize: '0.875rem', color: '#666' }}>
                Esta categoria e TODOS os seus produtos serão removidos do banco de dados para sempre.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <Button variant="outline" onClick={cancelDelete} style={{ flex: 1 }}>
                  Voltar
                </Button>
                <Button 
                  onClick={handleDeleteStep} 
                  style={{ flex: 1, backgroundColor: '#ef4444' }}
                >
                  Sim, continuar (2/3)
                </Button>
              </div>
            </div>
          )}

          {deleteConfirmStep === 3 && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ marginBottom: '1rem', color: '#ef4444', fontWeight: '500' }}>
                Última confirmação!
              </p>
              <p style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#666' }}>
                Digite <strong>DELETAR</strong> para confirmar a exclusão permanente:
              </p>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="Digite DELETAR"
                style={{ marginBottom: '1rem', textAlign: 'center', fontWeight: 'bold' }}
                data-testid="delete-confirm-input"
              />
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <Button variant="outline" onClick={cancelDelete} style={{ flex: 1 }}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleDeleteStep} 
                  disabled={deleteConfirmText !== 'DELETAR'}
                  style={{ 
                    flex: 1, 
                    backgroundColor: deleteConfirmText === 'DELETAR' ? '#ef4444' : '#ccc' 
                  }}
                >
                  EXCLUIR (3/3)
                </Button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div>
      <DeleteConfirmModal />
      
      {!showForm ? (
        <>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Categorias</h2>
            <Button onClick={() => setShowForm(true)} style={{ alignSelf: 'flex-start' }} data-testid="add-category-button">
              <Plus className="w-4 h-4 mr-2" />
              Nova Categoria
            </Button>
          </div>

          {/* Mobile Cards View */}
          <div className="categories-mobile" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {categories.map(category => (
              <div 
                key={category.id} 
                style={{ 
                  backgroundColor: category.ativo ? '#fff' : '#f5f5f5', 
                  borderRadius: '8px', 
                  padding: '1rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  opacity: category.ativo ? 1 : 0.7
                }}
                data-testid={`category-card-${category.id}`}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <h3 style={{ fontWeight: '600', fontSize: '1rem', color: category.ativo ? '#000' : '#666' }}>
                        {category.nome}
                      </h3>
                      {!category.ativo && (
                        <span style={{
                          backgroundColor: '#ef4444',
                          color: '#fff',
                          fontSize: '0.6rem',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          INATIVA
                        </span>
                      )}
                    </div>
                    <code style={{ 
                      backgroundColor: '#f5f5f5', 
                      padding: '0.125rem 0.375rem', 
                      borderRadius: '4px', 
                      fontSize: '0.75rem',
                      color: '#666'
                    }}>
                      /{category.slug}
                    </code>
                  </div>
                  <span style={{ 
                    backgroundColor: '#f0f0f0', 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '4px', 
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}>
                    Ordem: {category.ordem}
                  </span>
                </div>
                {category.descricao && (
                  <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.75rem' }}>
                    {category.descricao}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', borderTop: '1px solid #eee', paddingTop: '0.75rem', flexWrap: 'wrap' }}>
                  <Button 
                    variant={category.ativo ? "outline" : "default"}
                    size="sm" 
                    onClick={() => handleToggleCategory(category.id)}
                    style={{ 
                      backgroundColor: category.ativo ? 'transparent' : '#22c55e',
                      color: category.ativo ? '#666' : '#fff',
                      borderColor: category.ativo ? '#ddd' : '#22c55e'
                    }}
                    data-testid={`toggle-category-${category.id}`}
                  >
                    {category.ativo ? <PowerOff className="w-4 h-4 mr-1" /> : <Power className="w-4 h-4 mr-1" />}
                    {category.ativo ? 'Desativar' : 'Ativar'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleEdit(category)}
                    style={{ flex: 1 }}
                    data-testid={`edit-category-${category.id}`}
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => startDeleteProcess(category)}
                    style={{ color: '#ef4444' }}
                    data-testid={`delete-category-${category.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ backgroundColor: '#fff', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{editingCategory ? 'Editar Categoria' : 'Nova Categoria'}</h3>
            <Button variant="ghost" onClick={() => { setShowForm(false); setEditingCategory(null); }}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                data-testid="category-name-input"
                value={formData.nome}
                onChange={(e) => {
                  setFormData({ ...formData, nome: e.target.value, slug: generateSlug(e.target.value) });
                }}
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <Label htmlFor="slug">Slug (URL)</Label>
                <Input 
                  id="slug" 
                  data-testid="category-slug-input"
                  value={formData.slug} 
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })} 
                  placeholder="gerado-automaticamente" 
                />
              </div>
              <div>
                <Label htmlFor="ordem">Ordem</Label>
                <Input 
                  id="ordem" 
                  type="number" 
                  data-testid="category-ordem-input"
                  value={formData.ordem} 
                  onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })} 
                />
              </div>
            </div>

            <div>
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea 
                id="descricao" 
                data-testid="category-descricao-input"
                value={formData.descricao} 
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })} 
                rows={3} 
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingCategory(null); }}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-category-button">
                {editingCategory ? 'Atualizar' : 'Criar'} Categoria
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
